import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  ConferenceRoom,
  ConferenceService,
} from '../../services/conference.service';
import { EveCall } from '../../models/eve-call';
import { MatSnackBar } from '@angular/material/snack-bar';

import { SipService } from '../../services/sip.service';
import { LogModule, LogService } from 'src/app/services/log.service';

@Component({
  selector: 'app-conference-calls',
  templateUrl: './conference-calls.component.html',
  styleUrls: ['./conference-calls.component.css'],
})
export class ConferenceCallsComponent implements OnInit, OnDestroy {
  // @Input() conference!: ConferenceRoom;
  @Input() conferences!: ConferenceRoom[];
  constructor(
    private conferenceService: ConferenceService,
    private logService: LogService,
    private sipService: SipService,
    private snack: MatSnackBar
  ) {}

  ngOnInit() {}

  ngOnDestroy() {}

  async removeFromConference(call: EveCall): Promise<void> {
    try {
      await this.conferenceService.removeFromConference(call);

      const currentActiveCalls = this.sipService.getActiveCalls();
      // if find the call in active calls, update it to not in conference
      const updatedCalls = currentActiveCalls.map((c) => {
        if (c === call) {
          c.isInConference = false;
        }
        return c;
      });
      this.sipService.setActiveCalls(updatedCalls);
      this.snack.open(`Removed ${call.remoteUser} from conference`, 'Dismiss', {
        duration: 3000,
      });
    } catch (error: any) {
      const errorMessage = 'Failed to remove participant: ' + error.message;
      this.snack.open(errorMessage, 'Dismiss');
    }
  }

  getCallDuration(call: EveCall): string {
    const minutes = Math.floor(call.duration / 60);
    const seconds = call.duration % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`;
  }

  async toggleHold(call: EveCall) {
    if (call.isOnHold) {
      try {
        await call.unholdCall();
      } catch (error: any) {
        const errorMessage = 'Failed to unhold call: ' + error.message;
        this.snack.open(errorMessage, 'Dismiss');
      }
    } else {
      try {
        await call.holdCall();
      } catch (error: any) {
        const errorMessage = 'Failed to hold call: ' + error.message;
        this.snack.open(errorMessage, 'Dismiss');
      }
    }
  }

  async toggleMute(call: EveCall): Promise<void> {
    console.log(
      '🔧 DEBUG: toggleMute called for:',
      call.remoteUser,
      'isMuted:',
      call.isMuted
    );

    if (call.isMuted) {
      try {
        console.log('🔧 DEBUG: Attempting to unmute:', call.remoteUser);
        await this.conferenceService.unmuteParticipantInConference(call);
        console.log('🔧 DEBUG: Unmute successful for:', call.remoteUser);
      } catch (error: any) {
        console.error('🔧 DEBUG: Unmute failed for:', call.remoteUser, error);
        const errorMessage = 'Failed to unmute call: ' + error.message;
        this.snack.open(errorMessage, 'Dismiss');
      }
    } else {
      try {
        console.log('🔧 DEBUG: Attempting to mute:', call.remoteUser);
        await this.conferenceService.muteParticipantInConference(call);
        console.log('🔧 DEBUG: Mute successful for:', call.remoteUser);
      } catch (error: any) {
        console.error('🔧 DEBUG: Mute failed for:', call.remoteUser, error);
        const errorMessage = 'Failed to mute call: ' + error.message;
        this.snack.open(errorMessage, 'Dismiss');
      }
    }
  }

  async toggleConference(conference: ConferenceRoom): Promise<void> {
    if (conference.isOnHold) {
      try {
        await this.conferenceService.activateConference(conference);
      } catch (error) {
        const errorMessage = 'Failed to activate conference: ' + error;
        this.snack.open(errorMessage, 'Dismiss');
      }
    } else {
      try {
        await this.conferenceService.deactivateConference(conference);
      } catch (error) {
        const errorMessage = 'Failed to deactivate conference: ' + error;
        this.logService.logError(errorMessage, LogModule.SIP);
        this.snack.open(errorMessage, 'Dismiss');
      }
    }
  }

  /**
   * Toggle organizer mute for a specific conference
   */
  async toggleOrganizerMute(conference: ConferenceRoom): Promise<void> {
    try {
      this.logService.logInfo(
        `Toggling organizer mute for conference ${conference.name}`,
        LogModule.SIP
      );

      await this.conferenceService.toggleOrganizerMute(conference);

      const isNowMuted = this.conferenceService.isOrganizerMuted(conference);
      const message = isNowMuted
        ? `Organizer microphone muted`
        : `Organizer microphone unmuted`;

      this.snack.open(message, 'Dismiss', { duration: 3000 });

      this.logService.logInfo(
        `Organizer mute toggled successfully: ${
          isNowMuted ? 'MUTED' : 'UNMUTED'
        }`,
        LogModule.SIP
      );
    } catch (error: any) {
      const errorMessage = 'Failed to toggle organizer mute: ' + error.message;
      this.logService.logError(errorMessage, LogModule.SIP);
      this.snack.open(errorMessage, 'Dismiss', { duration: 5000 });
    }
  }

  /**
   * Check if organizer is muted in a specific conference
   */
  isOrganizerMuted(conference: ConferenceRoom): boolean {
    return this.conferenceService.isOrganizerMuted(conference);
  }

  async endConference(conference: ConferenceRoom): Promise<void> {
    try {
      await this.conferenceService.endConference(conference);

      this.logService.logInfo(
        `Conference ${conference.name} ended`,
        LogModule.SIP
      );
    } catch (error: any) {
      const errorMessage = 'Failed to end conference: ' + error.message;
      this.logService.logError(errorMessage, LogModule.SIP);
      console.error('Failed to end conference:', error);
    }
  }

  trackByConferenceId(index: number, conference: ConferenceRoom): string {
    return conference.id;
  }

  trackByParticipant(index: number, participant: EveCall): string {
    return participant.remoteUser;
  }
}
